import FormSubmitButton from "@/components/form-submit-button";
import { PersonalisationDropdown } from "@/components/personalisation-dropdown";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Toggle } from "@/components/ui/toggle";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  useCheckTemplate,
  useCreateTemplate,
  useDeleteTemplate,
  useUpdateTemplate,
} from "@/hooks/use-template";
import { personalisationChecker } from "@/lib/utils";
import TemplateChecker from "@/pages/templates/template-checker";
import { templateFormSchema } from "@/schemas/template";
import { useTemplateStore } from "@/stores/template.store";
import {
  TemplateCheckResponse,
  TemplateContentType,
  TemplateMetrics,
} from "@/types/template.types";
import { zodResolver } from "@hookform/resolvers/zod";
import MonacoEditor, { Monaco } from "@monaco-editor/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  ChevronDown,
  Code,
  Eye,
  Italic,
  List,
  ListOrdered,
  Strikethrough,
  Underline,
} from "lucide-react";
import Trash from "@/components/icons/trash";
import * as monaco from "monaco-editor";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { usePermission } from "@/hooks/use-premission";

export const Route = createFileRoute("/_private/templates/editor")({
  component: RouteComponent,
  validateSearch: (search) =>
    z
      .object({
        type: z.string().catch("plain_text").optional(),
        id: z.string().optional(),
      })
      .parse(search),
});

function RouteComponent() {
  const searchParams = Route.useSearch();
  const { template, clearStoredTemplate, report, clearReport, setReport } =
    useTemplateStore();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const [invalidPersonalisation, setInvalidPersonalisation] = useState<
    string[]
  >([]);

  const form = useForm<z.infer<typeof templateFormSchema>>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: {
      title: template?.title || "",
      subject: template?.subject || "",
      content: template?.content || "",
      contentType:
        template?.contentType ||
        (searchParams.type === TemplateContentType.HTML
          ? TemplateContentType.HTML
          : TemplateContentType.PLAIN_TEXT),
    },
  });

  const { mutateAsync: createTemplate, isPending: isCreating } =
    useCreateTemplate();
  const { mutateAsync: updateTemplate, isPending: isUpdating } =
    useUpdateTemplate();

  const { mutateAsync: deleteTemplate, isPending: isDeleting } =
    useDeleteTemplate();

  const { mutateAsync: checkTemplate, isPending: isChecking } =
    useCheckTemplate();

  const contentType = form.watch("contentType");

  async function onSubmit(values: z.infer<typeof templateFormSchema>) {
    try {
      const personalisation = personalisationChecker(
        values.content,
        values.subject,
      );

      if (personalisation.invalidTokens.length > 0) {
        setInvalidPersonalisation(personalisation.invalidTokens);
        toast.error("Invalid personalisation tokens");
        return;
      }

      if (searchParams.id) {
        await handleCheckTemplate();
        await updateTemplate({ id: searchParams.id, templateData: values });
        // clearStoredTemplate();
        toast.success("Template saved successfully");
        window.cioanalytics?.track("template_updated", {
          templateName: values.title,
          templateType: values.contentType,
        });
      } else {
        await handleCheckTemplate();
        await createTemplate({
          templateData: values,
          report: report ?? undefined,
        });
        toast.success("Template created successfully");
        window.cioanalytics?.track("template_created", {
          templateName: values.title,
          templateType: values.contentType,
        });
      }
      setInvalidPersonalisation([]);
      clearReport();
      // navigate({ to: "/templates" });
    } catch (error) {
      console.error(error);
      toast.error(
        `Failed to ${searchParams.id ? "update" : "create"} template`,
      );
    }
  }

  const handleDelete = async () => {
    try {
      await deleteTemplate(searchParams.id!);
      clearStoredTemplate();
      clearReport();
      toast.success("Template deleted successfully");
      window.cioanalytics?.track("template_deleted", {
        from: "editor",
        templateName: template?.title,
        templateType: template?.contentType,
      });
      navigate({ to: "/templates" });
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete template");
    }
  };
  const [reportDone, setReportDone] = useState(true);
  const [metrics, setMetrics] = useState<Partial<TemplateMetrics>>({});

  const setReportData = (response: TemplateCheckResponse) => {
    setMetrics({
      spamScore: response.score,
      wordCount: response.countData.wordCount.wordCount,
      // wordCount: 200,
      readabilityTime: response.countData.readTime.seconds,
      subjectLength: response.countData.subjectLength || 0,
      numberOfLinks: response.countData.links.count || 0,
      personalisation: response.countData.personalisation.allTokensCount || 0,
      questions: response.countData.questions.length || 0,
      brokenLinks: response.countData.links.details?.filter(
        (link) => !link.isValid,
      ).length,
      dangerousHtml: response.countData.dangerousHtml.count,
      shortenedUrls: response.countData.shortenedUrls.count,
      imageCount: response.countData.image.imageCount,
      emptyAltCount: response.countData.image.emptyAltCount,
      spamWords: response.countData.spamWords.count,
      upperCaseWords: response.countData.wordCount.upperCase.count,
      unsubscribeLink: response.countData.unsubscribeLink.have ? 1 : 0,
    });
    setReportDone(true);
    setReport(response);
  };

  useEffect(() => {
    if (searchParams.id && !template) {
      navigate({ to: "/templates" });
    }
  }, [template, searchParams.id, navigate]);

  const handleCheckTemplate = async () => {
    try {
      const content = form.getValues("content");
      const subject = form.getValues("subject");

      if (content.trim() === "") {
        toast.error("Content is empty");
        return;
      }

      const response = await checkTemplate({
        _id: searchParams.id,
        subject,
        content,
        contentType,
      });

      setReportData(response);

      window.cioanalytics?.track("template_checked", {
        templateName: form.getValues("title"),
        templateType: contentType,
      });
    } catch (error) {
      console.error("Error checking template:", error);
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-5 h-fit w-full">
      <Card className="shadow-none flex-1 p-4">
        {searchParams.id && (
          <CardHeader className="p-0 pb-6 flex flex-row space-y-0 justify-between items-center">
            <CardTitle>Edit Template</CardTitle>
            {hasPermission("templates:delete") && (
              <Button
                variant="ghost"
                size="icon"
                className="delete-button group"
                onClick={handleDelete}
                disabled={isDeleting}
                isLoading={isDeleting}
              >
                <Trash className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
        )}

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Template name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="Email subject"
                        className="pr-32"
                        {...field}
                      />
                      <PersonalisationDropdown
                        align="end"
                        className="absolute right-0 top-1/2 -translate-y-1/2 h-full shadow-none"
                        onSelect={(item) => {
                          field.onChange(field.value + item);
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  {contentType === TemplateContentType.HTML && (
                    <FormLabel>Email Body</FormLabel>
                  )}

                  <FormControl>
                    {/* @ts-ignore - field.value may be undefined */}
                    {contentType === TemplateContentType.PLAIN_TEXT ? (
                      <TextareaWithInsertion
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    ) : (
                      <TemplateHtmlContentEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-2 mt-4 justify-between items-center">
              <div className="flex gap-2">
                <span className="text-sm text-muted-foreground">
                  {contentType === TemplateContentType.PLAIN_TEXT
                    ? "Plain Text"
                    : "HTML"}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => form.reset()}
                >
                  Clear
                </Button>
                <FormSubmitButton
                  type="submit"
                  isEdit={!!searchParams.id}
                  disabled={isCreating || isUpdating || isChecking}
                  isPending={isCreating || isUpdating || isChecking}
                  addText="Create"
                  editText="Update"
                  addLoadingText="Creating..."
                  editLoadingText="Updating..."
                />
              </div>
            </div>
            {invalidPersonalisation.length > 0 && (
              <Alert variant="destructive">
                <AlertTitle>Invalid personalisation tokens</AlertTitle>
                <AlertDescription>
                  {invalidPersonalisation.length > 0 &&
                    invalidPersonalisation.join(", ")}
                </AlertDescription>
              </Alert>
            )}
          </form>
        </Form>
      </Card>
      <TemplateChecker
        form={form}
        metrics={metrics}
        setMetrics={setMetrics}
        isChecking={isChecking}
        report={report as TemplateCheckResponse}
        setReport={setReport}
        setReportData={setReportData}
        reportDone={reportDone}
        templateId={searchParams.id}
        handleCheckTemplate={handleCheckTemplate}
      />
    </div>
  );
}

const TemplateTextContentEditor = () => {
  return (
    <div className="flex flex-col gap-2">
      <Label>Content</Label>
      <div>
        <Card className="flex items-center gap-2 shadow-none p-2 rounded-lg mb-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary">
                Roboto <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {["Inter", "Roboto", "Arial", "Helvetica", "sans-serif"].map(
                (item) => (
                  <DropdownMenuItem key={item}>{item}</DropdownMenuItem>
                ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Separator orientation="vertical" className="h-5" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary">
                Heading 1 <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Heading 1</DropdownMenuItem>
              <DropdownMenuItem>Heading 2</DropdownMenuItem>
              <DropdownMenuItem>Heading 3</DropdownMenuItem>
              <DropdownMenuItem>Heading 4</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Separator orientation="vertical" className="h-5" />
          <div className="flex gap-2">
            <Toggle size="sm">
              <Bold />
            </Toggle>
            <Toggle size="sm">
              <Italic />
            </Toggle>
            <Toggle size="sm">
              <Underline />
            </Toggle>
            <Toggle size="sm">
              <Strikethrough />
            </Toggle>
          </div>
          <Separator orientation="vertical" className="h-5" />
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="p-1.5 min-w-8">
                  <AlignLeft />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="min-w-[--radix-dropdown-menu-trigger-width]"
                align="center"
              >
                <DropdownMenuItem>
                  <AlignLeft />
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <AlignCenter />
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <AlignRight />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* <ToggleGroup type="single" defaultValue="left">
          <ToggleGroupItem value="left" size="sm">
            <AlignLeft />
          </ToggleGroupItem>
          <ToggleGroupItem value="center" size="sm">
            <AlignCenter />
          </ToggleGroupItem>
          <ToggleGroupItem value="right" size="sm">
            <AlignRight />
          </ToggleGroupItem>
        </ToggleGroup> */}
          </div>
          <Separator orientation="vertical" className="h-5" />
          <div className="flex gap-2">
            <ToggleGroup type="single" defaultValue="list">
              <ToggleGroupItem value="list" size="sm">
                <List />
              </ToggleGroupItem>
              <ToggleGroupItem value="list-ordered" size="sm">
                <ListOrdered />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          <Separator orientation="vertical" className="h-5" />
          <PersonalisationDropdown
            onSelect={(item) => {
              console.log(item);
            }}
          />
        </Card>
        <Textarea rows={12} placeholder="Write your email content here..." />
      </div>
    </div>
  );
};

const TemplateHtmlContentEditor = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);

  const [contentType, setContentType] = useState<"code" | "preview">("code");

  const handleEditorDidMount = (
    editor: monaco.editor.IStandaloneCodeEditor,
    monaco: Monaco,
  ) => {
    editorRef.current = editor; // Store the editor instance
    monacoRef.current = monaco; // Store the monaco instance
  };

  const insertTextAtCursor = (text: string) => {
    if (editorRef.current && monacoRef.current) {
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      const position = editor.getPosition();
      const range = new monaco.Range(
        position?.lineNumber || 0,
        position?.column || 0,
        position?.lineNumber || 0,
        position?.column || 0,
      );
      editor.executeEdits("insert-text", [
        {
          range,
          text,
          forceMoveMarkers: true,
        },
      ]);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <ToggleGroup
          type="single"
          size="sm"
          value={contentType}
          onValueChange={(value) => setContentType(value as "code" | "preview")}
        >
          <ToggleGroupItem
            value="code"
            aria-label="Plain text mode"
            className=" px-2 py-1 rounded-sm text-xs h-auto"
          >
            <Code size={16} />
            Code
          </ToggleGroupItem>
          <ToggleGroupItem
            value="preview"
            aria-label="HTML mode"
            className="px-2 py-1 rounded-sm text-xs h-auto"
          >
            <Eye size={16} />
            Preview
          </ToggleGroupItem>
        </ToggleGroup>

        <PersonalisationDropdown
          align="end"
          onSelect={(item) => {
            insertTextAtCursor(item);
          }}
        />
      </div>
      {contentType === "code" ? (
        <MonacoEditor
          onMount={handleEditorDidMount}
          height="500px"
          language="handlebars"
          theme="vs-dark"
          value={value}
          onChange={(value) => onChange(value || "")}
          className="rounded-lg overflow-hidden"
        />
      ) : (
        <div className="h-[500px] w-full bg-slate-100 rounded-lg">
          <iframe
            srcDoc={value}
            className="w-full h-full border rounded-lg"
            sandbox="allow-same-origin allow-scripts"
            title="Template Preview"
          />
        </div>
      )}
    </div>
  );
};

const TextareaWithInsertion = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertTextAtCursor = (textToInsert: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;

    // Insert text at cursor position
    const newText =
      value.substring(0, startPos) + textToInsert + value.substring(endPos);

    onChange(newText); // Notify parent about the change

    // Restore cursor position after insertion
    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd =
        startPos + textToInsert.length;
      textarea.focus();
    });
  };

  return (
    <div className="flex flex-col items-center  gap-2">
      <div className="flex justify-between items-center w-full">
        <Label>Email Body</Label>
        <PersonalisationDropdown onSelect={insertTextAtCursor} />
      </div>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={20}
        className="w-full"
      />
    </div>
  );
};
